import { Form, Formik } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdHelpOutline } from 'react-icons/md'
import * as Yup from 'yup'
import * as ContactAPI from '../../lib/API/ContactAPI'
import Headline from '../Elements/Headline'
import { Modal } from '../Elements/Modal'
import Text from '../Elements/Text'
import Button from '../Formik/Button'
import Input from '../Formik/Input'
import Textarea from '../Formik/Textarea'

interface Props {
  className?: string
  showLabel?: boolean
}

const ContactModal = ({ className = '', showLabel = false }: Props) => {
  const { t } = useTranslation('common')
  const [showModal, toggleModal] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [hasError, setHasError] = useState(false)

  const closeAndReset = () => {
    toggleModal(false)
    setIsSent(false)
    setHasError(false)
  }

  return (
    <>
      <button
        type='button'
        onClick={() => toggleModal(true)}
        title={t('common:contact.navigationLabel')}
        aria-label={t('common:contact.navigationLabel')}
        className={`flex items-center text-white hover:text-theme-white rounded-md text-sm font-medium ${className}`}
      >
        <MdHelpOutline size='1.5em' />
        {showLabel && (
          <span className='ml-2'>{t('common:contact.navigationLabel')}</span>
        )}
      </button>

      <Modal show={showModal} onHide={closeAndReset}>
        {(closeModal: () => void) =>
          isSent
            ? (
              <>
                <Headline>{t('common:contact.successHeading')}</Headline>
                <Text className='mt-6 mb-6'>
                  {t('common:contact.successBody')}
                </Text>
                <div className='flex justify-end'>
                  <Button onClick={closeModal}>
                    {t('common:contact.close')}
                  </Button>
                </div>
              </>
            )
            : (
              <Formik
                initialValues={{ title: '', message: '' }}
                validationSchema={Yup.object({
                  title: Yup.string().required(t('common:validation.required')),
                  message: Yup.string().required(
                    t('common:validation.required'),
                  ),
                })}
                onSubmit={async (values, { resetForm, setSubmitting }) => {
                  setHasError(false)
                  try {
                    await ContactAPI.sendContactRequest(values)
                    resetForm()
                    setIsSent(true)
                  } catch {
                    setHasError(true)
                  } finally {
                    setSubmitting(false)
                  }
                }}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <h2 className='text-xl font-semibold mb-2'>
                      {t('common:contact.heading')}
                    </h2>
                    <p className='mb-5 text-gray-600'>
                      {t('common:contact.description')}
                    </p>
                    <p className='mb-2'>{t('common:contact.titleLabel')}</p>
                    <Input
                      name='title'
                      placeholder={t('common:contact.titlePlaceholder')}
                    />
                    <p className='mt-5 mb-2'>
                      {t('common:contact.commentLabel')}
                    </p>
                    <Textarea
                      name='message'
                      placeholder={t('common:contact.commentPlaceholder')}
                    />
                    {hasError && (
                      <p className='mt-5 text-sm text-red-500'>
                        {t('common:contact.error')}
                      </p>
                    )}
                    <Button type='submit' className='mt-5' loading={isSubmitting}>
                      {t('common:contact.submit')}
                    </Button>
                  </Form>
                )}
              </Formik>
            )
        }
      </Modal>
    </>
  )
}

export default ContactModal
